import React, { useEffect, useState } from 'react';
import SEO from '../SEO';
import { ArrowLeft, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import LegalNavigation from '../LegalNavigation';
import { useCookies } from '../../contexts/CookieContext';

// Cookie Settings Modal Component
const CookieSettingsModal = ({ isOpen, onClose, onSave, currentPreferences }) => {
  const [preferences, setPreferences] = useState(currentPreferences);

  useEffect(() => {
    setPreferences(currentPreferences);
  }, [currentPreferences]);

  const handlePreferenceChange = (type, value) => {
    setPreferences(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const handleSave = () => {
    onSave(preferences);
  };

  const cookieTypes = [
    {
      key: 'necessary',
      title: 'Cookies nécessaires',
      description: 'Ces cookies sont indispensables au fonctionnement du site. Ils ne peuvent pas être désactivés.',
      required: true
    },
    {
      key: 'statistics',
      title: 'Cookies statistiques',
      description: 'Ces cookies nous permettent de mesurer l\'audience du site et d\'améliorer ses performances.',
      required: false
    },
    {
      key: 'marketing',
      title: 'Cookies marketing',
      description: 'Ces cookies permettent de personnaliser la publicité et les contenus en fonction de vos intérêts.',
      required: false
    },
    {
      key: 'external',
      title: 'Cookies tiers',
      description: 'Ces cookies proviennent de services externes (YouTube, réseaux sociaux, cartes, etc.).',
      required: false
    }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Settings className="w-6 h-6 text-[#4b9fcc] mr-3" />
            <h2 className="text-xl font-bold text-gray-900">Personnaliser mes choix</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 mb-6">
            Gérez vos préférences de cookies. Vous pouvez activer ou désactiver différents types de cookies selon vos préférences.
          </p>

          <div className="space-y-6">
            {cookieTypes.map((type) => (
              <div key={type.key} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-900">{type.title}</h3>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences[type.key]}
                      onChange={(e) => handlePreferenceChange(type.key, e.target.checked)}
                      disabled={type.required}
                      className="sr-only"
                    />
                    <div className={`w-11 h-6 rounded-full transition-colors duration-200 ease-in-out ${
                      preferences[type.key] ? 'bg-[#4b9fcc]' : 'bg-gray-300'
                    } ${type.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                      <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ease-in-out ${
                        preferences[type.key] ? 'translate-x-5' : 'translate-x-0.5'
                      } mt-0.5`} />
                    </div>
                  </label>
                </div>
                <p className="text-sm text-gray-600">{type.description}</p>
                {type.required && (
                  <p className="text-xs text-[#4b9fcc] mt-1 font-medium">Obligatoire</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            Fermer
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-[#4b9fcc] text-white rounded-lg hover:bg-[#e71f74] transition-colors duration-300"
          >
            Enregistrer mes choix
          </button>
        </div>
      </div>
    </div>
  );
};

const GestionCookies = () => {
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const { cookiePreferences, saveCustomPreferences } = useCookies();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleOpenSettings = () => {
    setShowSettingsModal(true);
  };

  const handleCloseSettings = () => {
    setShowSettingsModal(false);
  };

  const handleSavePreferences = (preferences) => {
    saveCustomPreferences(preferences);
    setShowSettingsModal(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Gestion des Cookies"
        description="Gérez vos préférences de cookies sur SISENS. Personnalisez les cookies selon vos besoins : cookies nécessaires, statistiques, marketing."
        keywords="gestion cookies, préférences cookies, SISENS, cookies site web"
        url="https://sisens.eu/gestion-cookies"
      />
      {/* Navigation bar */}
      <LegalNavigation />
      
      <div className="pt-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Bouton retour */}
        <div className="mb-8">
          <Link 
            to="/"
            className="inline-flex items-center text-[#4b9fcc] hover:text-[#e71f74] transition-colors duration-300"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à l'accueil
          </Link>
        </div>

        {/* Contenu de la gestion des cookies */}
        <div className="prose prose-lg max-w-none">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            🍪 Gestion des cookies
          </h1>
          <p className="text-sm text-gray-600 mb-8 italic">
            Mise à jour le 8 octobre 2025
          </p>

          {/* Section 1 - Qu'est-ce qu'un cookie */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-[#4b9fcc]">
              1. Qu'est-ce qu'un cookie ?
            </h2>
            <p className="mb-4">
              Un cookie est un petit fichier texte déposé sur votre terminal (ordinateur, tablette, smartphone) lors de la consultation d'un site internet.
            </p>
            <p className="mb-4">
              Il permet au site de reconnaître votre appareil, de mémoriser vos préférences et d'améliorer votre expérience utilisateur.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg mb-4">
              <p className="text-blue-800">
                <span className="font-semibold">📍 Important :</span> Les cookies ne permettent pas d'identifier directement une personne, mais certains peuvent être considérés comme des données personnelles, car ils permettent de suivre votre navigation.
              </p>
            </div>
          </section>

          {/* Section 2 - Qui dépose les cookies */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-[#4b9fcc]">
              2. Qui dépose les cookies ?
            </h2>
            <p className="mb-4">
              Les cookies déposés sur le site <span className="font-semibold">https://www.sisens.eu</span> proviennent :
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2 mb-4">
              <li><span className="font-semibold">de SISENS</span>, pour le fonctionnement technique du site,</li>
              <li><span className="font-semibold">et de tiers</span> (par exemple, outils de mesure d'audience ou réseaux sociaux) lorsque vous visionnez un contenu externe intégré (YouTube, LinkedIn, etc.).</li>
            </ul>
          </section>

          {/* Section 3 - Types de cookies */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-[#4b9fcc]">
              3. Types de cookies utilisés sur le site
            </h2>
            
            {/* Cookies nécessaires */}
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                🔹 a) Cookies strictement nécessaires (exemptés de consentement)
              </h3>
              <p className="mb-3">
                Ces cookies sont indispensables au bon fonctionnement du site et ne peuvent pas être désactivés.
              </p>
              <p className="mb-2">Ils permettent notamment :</p>
              <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
                <li>d'assurer la sécurité du site (session, pare-feu, etc.),</li>
                <li>de mémoriser vos choix de consentement,</li>
                <li>d'accéder à des zones sécurisées.</li>
              </ul>
              <div className="bg-green-50 p-3 rounded-lg">
                <p className="text-green-800">
                  ➡️ <span className="font-semibold">Ces cookies ne nécessitent aucun consentement préalable.</span>
                </p>
              </div>
            </div>

            {/* Cookies de mesure */}
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                🔹 b) Cookies de mesure d'audience
              </h3>
              <p className="mb-3">
                Ces cookies permettent à SISENS de mesurer la fréquentation du site (nombre de visiteurs, pages consultées, temps de visite…) et d'en améliorer le contenu.
              </p>
              <p className="mb-3">
                Les données collectées sont anonymisées et non utilisées à des fins publicitaires.
              </p>
              <div className="bg-yellow-50 p-3 rounded-lg">
                <p className="text-yellow-800">
                  ➡️ <span className="font-semibold">Ces cookies sont soumis à votre consentement préalable.</span>
                </p>
              </div>
            </div>

            {/* Cookies tiers */}
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                🔹 c) Cookies tiers
              </h3>
              <p className="mb-3">
                Certains services externes peuvent déposer leurs propres cookies sur votre terminal lorsque vous naviguez sur le site (lecture d'une vidéo, partage sur les réseaux sociaux, etc.).
              </p>
              <p className="mb-2">Ces cookies permettent :</p>
              <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
                <li>de visualiser des contenus intégrés (YouTube, Vimeo, etc.),</li>
                <li>de partager des contenus sur les réseaux sociaux (LinkedIn, Facebook, etc.).</li>
              </ul>
              <div className="bg-orange-50 p-3 rounded-lg">
                <p className="text-orange-800 mb-2">
                  ➡️ <span className="font-semibold">Ces cookies nécessitent également votre consentement.</span>
                </p>
                <p className="text-orange-800 text-sm">
                  SISENS n'a aucun contrôle sur le dépôt ou l'utilisation de ces cookies par des tiers.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4 - Consentement */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-[#4b9fcc]">
              4. Consentement au dépôt des cookies
            </h2>
            <p className="mb-4">
              Lors de votre première visite sur le site, un bandeau d'information s'affiche pour vous informer de la présence de cookies et vous proposer de :
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <ul className="list-disc list-inside space-y-2">
                <li><span className="font-semibold">Tout accepter</span>,</li>
                <li><span className="font-semibold">Tout refuser</span>,</li>
                <li><span className="font-semibold">Personnaliser vos choix</span>.</li>
              </ul>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg mb-4">
              <p className="text-blue-800">
                <span className="font-semibold">🔒 Garantie :</span> Aucun cookie non essentiel n'est déposé sans votre action positive (clic sur "Accepter").
              </p>
            </div>
            <p>
              Votre choix est conservé pendant <span className="font-semibold">6 mois</span>, puis le bandeau vous sera à nouveau présenté.
            </p>
          </section>

          {/* Section 5 - Gestion et suppression */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-[#4b9fcc]">
              5. Gestion ou suppression des cookies
            </h2>
            <p className="mb-4">Vous pouvez à tout moment :</p>
            
            <div className="mb-4">
              <p className="mb-3">
                <span className="font-semibold">• Modifier vos préférences</span> via le lien <span className="text-[#4b9fcc] font-semibold">"Gestion des cookies"</span> disponible en bas de chaque page du site,
              </p>
              <p className="mb-3">
                <span className="font-semibold">• Supprimer les cookies</span> déjà enregistrés en configurant votre navigateur :
              </p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <h4 className="font-semibold mb-3 text-gray-900">📖 Guides par navigateur :</h4>
              <ul className="space-y-2">
                <li>
                  <span className="font-semibold">Chrome :</span> 
                  <a href="https://support.google.com/chrome/answer/95647?hl=fr" 
                     target="_blank" 
                     rel="noopener noreferrer" 
                     className="text-[#4b9fcc] hover:text-[#e71f74] ml-2 underline">
                    https://support.google.com/chrome/answer/95647?hl=fr
                  </a>
                </li>
                <li>
                  <span className="font-semibold">Firefox :</span> 
                  <a href="https://support.mozilla.org/fr/kb/effacer-cookies-supprimer-infos-sites" 
                     target="_blank" 
                     rel="noopener noreferrer" 
                     className="text-[#4b9fcc] hover:text-[#e71f74] ml-2 underline">
                    https://support.mozilla.org/fr/kb/effacer-cookies-supprimer-infos-sites
                  </a>
                </li>
                <li>
                  <span className="font-semibold">Safari :</span> 
                  <a href="https://support.apple.com/fr-fr/guide/safari/sfri11471/mac" 
                     target="_blank" 
                     rel="noopener noreferrer" 
                     className="text-[#4b9fcc] hover:text-[#e71f74] ml-2 underline">
                    https://support.apple.com/fr-fr/guide/safari/sfri11471/mac
                  </a>
                </li>
                <li>
                  <span className="font-semibold">Edge :</span> 
                  <a href="https://support.microsoft.com/fr-fr/help/4027947" 
                     target="_blank" 
                     rel="noopener noreferrer" 
                     className="text-[#4b9fcc] hover:text-[#e71f74] ml-2 underline">
                    https://support.microsoft.com/fr-fr/help/4027947
                  </a>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 6 - Liste des cookies */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-[#4b9fcc]">
              6. Liste des cookies utilisés sur le site
            </h2>
            <p className="mb-4 text-sm text-gray-600 italic">
              (La liste doit être ajustée selon les cookies effectivement utilisés sur le site.)
            </p>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300 text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Nom du cookie</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Fournisseur</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Finalité</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Durée de conservation</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Consentement requis</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 font-mono">sisens_cookie_consent</td>
                    <td className="border border-gray-300 px-4 py-2">SISENS</td>
                    <td className="border border-gray-300 px-4 py-2">Sauvegarde du choix de consentement</td>
                    <td className="border border-gray-300 px-4 py-2">6 mois</td>
                    <td className="border border-gray-300 px-4 py-2 text-center">❌ Non</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2 font-mono">sisens_cookie_preferences</td>
                    <td className="border border-gray-300 px-4 py-2">SISENS</td>
                    <td className="border border-gray-300 px-4 py-2">Sauvegarde des préférences de cookies</td>
                    <td className="border border-gray-300 px-4 py-2">6 mois</td>
                    <td className="border border-gray-300 px-4 py-2 text-center">❌ Non</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 font-mono">_ga</td>
                    <td className="border border-gray-300 px-4 py-2">Google Analytics</td>
                    <td className="border border-gray-300 px-4 py-2">Mesure d'audience du site</td>
                    <td className="border border-gray-300 px-4 py-2">13 mois</td>
                    <td className="border border-gray-300 px-4 py-2 text-center">✅ Oui</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2 font-mono">_gid</td>
                    <td className="border border-gray-300 px-4 py-2">Google Analytics</td>
                    <td className="border border-gray-300 px-4 py-2">Statistiques de navigation</td>
                    <td className="border border-gray-300 px-4 py-2">24 heures</td>
                    <td className="border border-gray-300 px-4 py-2 text-center">✅ Oui</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 font-mono">_gat</td>
                    <td className="border border-gray-300 px-4 py-2">Google Analytics</td>
                    <td className="border border-gray-300 px-4 py-2">Limitation du taux de requêtes</td>
                    <td className="border border-gray-300 px-4 py-2">1 minute</td>
                    <td className="border border-gray-300 px-4 py-2 text-center">✅ Oui</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2 font-mono">yt-player, CONSENT</td>
                    <td className="border border-gray-300 px-4 py-2">YouTube</td>
                    <td className="border border-gray-300 px-4 py-2">Lecture de vidéos intégrées</td>
                    <td className="border border-gray-300 px-4 py-2">6 mois à 2 ans</td>
                    <td className="border border-gray-300 px-4 py-2 text-center">✅ Oui</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section de personnalisation */}
          <section className="mb-8">
            <div className="bg-[#4b9fcc] text-white p-6 rounded-lg">
              <h3 className="text-xl font-bold mb-4">🔧 Personnaliser mes préférences</h3>
              <p className="mb-4">
                Vous pouvez modifier vos préférences de cookies à tout moment en cliquant sur le bouton ci-dessous.
              </p>
              <button
                onClick={handleOpenSettings}
                className="inline-flex items-center px-6 py-3 bg-white text-[#4b9fcc] font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-300"
              >
                <Settings className="w-5 h-5 mr-2" />
                Personnaliser mon choix
              </button>
            </div>
          </section>

          {/* Section informative finale */}
          <section className="mb-8">
            <div className="bg-gray-100 p-6 rounded-lg">
              <h3 className="text-xl font-bold mb-3 text-gray-900">💬 Besoin d'aide ?</h3>
              <p className="mb-2 text-gray-700">
                Pour toute question sur la gestion des cookies ou vos données personnelles :
              </p>
              <p className="text-gray-700">
                📧 Contactez-nous à : <a href="mailto:contact@sisens.eu" className="font-semibold underline hover:text-[#4b9fcc] text-[#4b9fcc]">contact@sisens.eu</a>
              </p>
            </div>
          </section>
        </div>
      </div>
      </div>

      {/* Cookie Settings Modal */}
      <CookieSettingsModal
        isOpen={showSettingsModal}
        onClose={handleCloseSettings}
        onSave={handleSavePreferences}
        currentPreferences={cookiePreferences}
      />
    </div>
  );
};

export default GestionCookies;